import { HttpContext } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  CACHE_OPTIONS,
  CacheOptions,
  GenericRestApiOKResponse,
  RestApiClientService,
} from '@troyai/rest-api-client';
import { map } from 'rxjs';
import { ClinicalObservation } from './models/clinical-observation.interface';
import { CreateOrEditClinicalObservationPayload } from './models/new-clinical-observation-payload.interface';
import { StandardMedicalCodeItem } from './models/standard-medical-code-item.interface';

@Injectable({
  providedIn: 'root',
})
export class ClinicalObservationsApiService {
  private restApiService = inject(RestApiClientService);

  getClinicalObservations(globalId: number) {
    return this.restApiService
      .get<ClinicalObservation[]>(`members/${globalId}/clinical-observations`)
      .pipe(
        map((clinicalObservations) => {
          if (!clinicalObservations || clinicalObservations.length === 0) {
            return [];
          }
          return clinicalObservations;
        })
      );
  }

  getSingleClinicalObservation(memberGlobalId: number, clinicalObservationId: number) {
    return this.restApiService.get<ClinicalObservation>(
      `members/${memberGlobalId}/clinical-observations/${clinicalObservationId}`
    );
  }

  addNewClinicalObservation(
    memberGlobalId: number,
    payload: CreateOrEditClinicalObservationPayload[]
  ) {
    // TODO: Handle the array, the current setup will always put just one item in this array
    // This map exists just for the reason that the POST request requires an array as a payload
    const adaptedPayload = payload.map((item) => {
      return {
        test: item.test,
        value: item.value,
        comment: item.comment,
        source: item.source,
        date_of_service: item.dateOfService,
        standard_code: item.standard_code,
      };
    });
    return this.restApiService
      .post<ClinicalObservation>(`members/${memberGlobalId}/clinical-observations`, adaptedPayload)
      .pipe(
        map((result) => {
          // TODO: Handle the array in the UI, currently only working with one clinical obs. is supported
          if (result && Array.isArray(result) && result.length > 0) {
            return result[0];
          }

          return;
        })
      );
  }

  editClinicalObservation(
    memberGlobalId: number,
    clinicalObservationId: number,
    payload: CreateOrEditClinicalObservationPayload
  ) {
    return this.restApiService.patch<GenericRestApiOKResponse>(
      `members/${memberGlobalId}/clinical-observations/${clinicalObservationId}`,
      {
        test: payload.test,
        value: payload.value,
        comment: payload.comment,
        source: payload.source,
        date_of_service: payload.dateOfService,
        standard_code: payload.standard_code,
      }
    );
  }

  getStandardMedicalCode(code: string, cacheOptions?: CacheOptions) {
    const context = new HttpContext();
    if (cacheOptions) {
      context.set(CACHE_OPTIONS, cacheOptions);
    }

    return this.restApiService.get<StandardMedicalCodeItem>(`codes/${code}`, {
      context,
    });
  }
}
